import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {ColorDot} from '../../../../../libs/shared/ui/src/components/utility/colorDot';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {lightTheme} from '../../../../../libs/shared/ui/src/theme/light/lightTheme';

/*
 * Props.
 */

type Color = string | undefined;

interface FormattingColorPickerDropdownProps {
  dropdownProps: DropdownProps;
  colors: ReadonlyArray<ReadonlyArray<ColorDescription>>;
  selectedColor: Color;
  onColorSelect: (color: Color) => void;
}

interface ColorDescription {
  color: Color;
  borderColor?: Color;
  selectedBorderColor?: Color;
  lightForeground?: boolean;
}

interface ColorPickerItemProps {
  colorDescription: ColorDescription;
  isSelected: boolean;
  onClick: () => void;
}

/*
 * Style.
 */

interface PickerStyleProps {
  $colorsPerRow: number;
}
const StyledPickerDiv = styled.div<PickerStyleProps>`
  display: grid;
  grid-gap: 8px;
  padding: 12px;
  grid-template-columns: repeat(${(p) => p.$colorsPerRow}, 1fr);
`;

const StyledColorPickerItemWrapper = styled.div`
  display: flex;
  ${center()};
  position: relative;
  width: 20px;
  height: 20px;
`;

const StyledColorDot = styled(ColorDot)`
  position: absolute;
  transition: transform 120ms;

  &:hover {
    transform: scale(1.2);
  }
`;

const StyledIcon = styled(Icon)`
  z-index: 1;
  pointer-events: none;
`;

/*
 * Component.
 */

export const FormattingColorPickerDropdown: FC<FormattingColorPickerDropdownProps> = (props) => {
  const {dropdownProps, colors, selectedColor, onColorSelect} = props;

  return (
    <Dropdown {...dropdownProps}>
      <StyledPickerDiv $colorsPerRow={colors[0] ? colors[0].length : 0}>
        {_(colors)
          .flatten()
          .map((colorDescription) => (
            <ColorPickerItem
              key={colorDescription.color || 'blank'}
              colorDescription={colorDescription}
              isSelected={colorDescription.color === selectedColor}
              onClick={() => onColorSelect(colorDescription.color)}
            />
          ))
          .value()}
      </StyledPickerDiv>
    </Dropdown>
  );
};

const ColorPickerItem: FC<ColorPickerItemProps> = (props) => {
  const {colorDescription, isSelected, onClick} = props;
  const {color, borderColor, selectedBorderColor, lightForeground} = colorDescription;

  const darkCheckmarkColor = lightTheme.alphas.black60;
  const lightCheckmarkColor = lightTheme.alphas.white60;

  return (
    <StyledColorPickerItemWrapper onClick={onClick} onMouseDown={doNotStealFocus}>
      <StyledColorDot
        color={color || '#fff'}
        borderColor={isSelected ? selectedBorderColor : borderColor}
        size={20}
      />
      {isSelected && (
        <StyledIcon
          name="checkmarkSelectedSmall"
          color={lightForeground ? lightCheckmarkColor : darkCheckmarkColor}
        />
      )}
    </StyledColorPickerItemWrapper>
  );
};

function doNotStealFocus(evt: React.SyntheticEvent) {
  evt.preventDefault();
}
